// Chat telemetry sink. Single entry point for the SDK error
// breadcrumbs the matrix-sdk-rust adapter records. Wraps `@sentry/browser`
// behind a thin facade so every call site (#3073) goes through the
// same scrubbing + DSN/test/dev gate, and so swapping sinks later is
// a one-file change.
//
// Initialisation gate: `Sentry.init` runs only when a runtime DSN is
// present AND we're not in dev / vitest / Storybook. Missing DSN ≡
// no telemetry — `recordSdkBreadcrumb` and `captureSdkException`
// become silent no-ops. The `@sentry/browser` `addBreadcrumb` /
// `captureException` calls themselves are no-ops before init, but
// we short-circuit at the helper boundary too so the intent is
// explicit and a misconfigured tree never accidentally posts.
//
// Privacy boundary: every breadcrumb passes through `scrubBreadcrumbData`
// which keeps a small allowlist of low-sensitivity fields (event id,
// room id, error message). Message bodies, recovery keys, account
// data, and full session JSON never leave the helper.

import * as Sentry from "@sentry/browser";

/**
 * Stable category strings for SDK breadcrumbs. The category is the
 * primary axis Sentry's UI clusters by, so we keep these short and
 * stable. One per instrumented warn site in
 * `~/lib/matrix-sdk-rust.ts` — see the inline comments at each call
 * site for the mapping.
 */
export type SdkBreadcrumbCategory =
  | "matrix.verification"
  | "matrix.member-fetch"
  | "matrix.notification-level"
  | "matrix.receipt-poll"
  | "matrix.own-receipt-poll"
  | "matrix.own-thread-receipt-poll"
  | "matrix.session"
  | "matrix.sync"
  | "matrix.thread-refresh"
  | "matrix.typing"
  | "matrix.receipts-for-event"
  | "matrix.refresh-token"
  | "matrix.meeting.url-rejected"
  | "matrix.url-preview"
  | "matrix.slash"
  | "matrix.decryption-reports"
  | "matrix.backup-restore"
  | "matrix.utd-reasons"
  | "matrix.uia"
  | "chat.keychain.unavailable"
  | "chat.keychain.transient"
  | "chat.keychain.retry-relaunch"
  | "chat.boot";

/**
 * High-signal failure phases that justify a captured exception (not
 * just a breadcrumb). These are the phases the ticket calls out:
 * UTD recovery, sync transitions to `"error"`, and unexpected
 * rejections from `loginWithAccessToken` / OIDC login / `restoreSession`.
 */
export type SdkExceptionPhase =
  | "matrix.sync.error-state"
  | "matrix.utd-recovery"
  | "matrix.login-with-access-token"
  | "matrix.start-oidc-login"
  | "matrix.finish-oidc-login"
  | "matrix.restore-oidc-inflight"
  | "matrix.restore-session"
  | "matrix.uia"
  | "chat.shell.render-error";

/** Minimal breadcrumb data shape. Strings/numbers/booleans only. */
export type SdkBreadcrumbData = Record<string, string | number | boolean | null>;

let initialised = false;

/**
 * Whether the telemetry runtime is active. Helpers short-circuit when
 * false. Tests can flip this with `__setTelemetryActiveForTests`.
 */
function isActive(): boolean {
  return initialised;
}

const SECRET_PATTERNS: ReadonlyArray<readonly [RegExp, string]> = [
  [/\b(?:syt|mct|mat|mar|syr)_[A-Za-z0-9_-]{8,}/g, "<token>"],
  [/\b(?:[A-Za-z0-9]{4}\s){7,11}[A-Za-z0-9]{4}\b/g, "<recovery-key>"],
  [/mxc:\/\/\S+/g, "<mxc>"],
  [/https?:\/\/[^\s"')]+/g, "<url>"],
  [/[@!#$][A-Za-z0-9._=\-/+]+:[A-Za-z0-9.-]+/g, "<matrix-id>"],
  [/\$[A-Za-z0-9_+/=-]{20,}/g, "<event-id>"],
];

function scrubText(text: string): string {
  let out = text;
  for (const [pattern, replacement] of SECRET_PATTERNS) out = out.replace(pattern, replacement);
  return out;
}

/**
 * Last stop before an event leaves the device: strip user and request
 * data and replace tokens, recovery keys, Matrix ids and URLs in every
 * message the event carries.
 */
export function scrubSentryEvent<T extends Sentry.ErrorEvent>(event: T): T {
  delete event.user;
  delete event.request;
  if (typeof event.message === "string") event.message = scrubText(event.message);
  for (const value of event.exception?.values ?? []) {
    if (typeof value.value === "string") value.value = scrubText(value.value);
  }
  return event;
}

/**
 * Initialise the Sentry browser SDK. Idempotent — repeated calls are
 * a no-op. Skips initialisation when:
 *   - the resolved DSN is empty (dev / storybook / test, or a host
 *     deployment that opted out);
 *   - `import.meta.env.DEV` is true (Vite dev server, Storybook);
 *   - `import.meta.env.MODE === "test"` (vitest config sets this);
 *   - `import.meta.env.VITEST` is set (defensive — vitest exposes
 *     this regardless of `mode`).
 *
 * The DSN is read at call time from a runtime-supplied value. The
 * standalone shell reads it from `/config.json` via `loadConfig()`
 * in `main.tsx`; embed hosts can wire their own DSN through a future
 * host prop (see #3073 design notes).
 */
export function initSdkTelemetry(opts: { dsn: string | undefined }): void {
  if (initialised) return;
  const dsn = opts.dsn?.trim();
  if (!dsn) return;
  if (import.meta.env.DEV) return;
  if (import.meta.env.MODE === "test") return;
  // Vitest sets `import.meta.env.VITEST` even when the `mode` is
  // overridden (e.g. happy-dom presets). Typed in
  // `~/vite-env.d.ts` so this read needs no cast.
  if (import.meta.env.VITEST) return;

  // Disable the browser SDK's default integration set entirely —
  // `defaultIntegrations: false` is the option that actually replaces
  // the defaults, not the array form (Sentry merges `integrations`
  // with defaults unless this option is set; verified against
  // `@sentry/browser` 10.51.0 `packages/browser/src/sdk.ts` and
  // `packages/core/src/integration.ts → getIntegrationsToSetup`).
  // Without this, `breadcrumbsIntegration` would auto-capture every
  // site-wide `console.*`, `fetch`/XHR URL, and DOM event;
  // `globalHandlersIntegration` would capture every uncaught error /
  // unhandledrejection app-wide; `httpContextIntegration` would
  // attach the request URL to every event. None of those go through
  // `scrubBreadcrumbData`, which would silently bypass the privacy
  // contract this module exists to enforce — every other
  // `console.warn` site in the chat surface (post-login UIA,
  // notifications and browser integration reads would start shipping its
  // argument list verbatim, and every Matrix homeserver request URL
  // (room ids, event ids, sync tokens) would become a
  // `category: "http"` breadcrumb.
  //
  // `globalHandlersIntegration` is the one default we add back on purpose:
  // without it an uncaught error or unhandled rejection never reached
  // Bugsink, so crashes outside the instrumented SDK call sites were
  // invisible. It only reports the error; breadcrumbs and request
  // context stay off, and `scrubSentryEvent` removes ids, tokens and
  // keys from the message before anything leaves the device.
  //
  // The explicit `integrations` list below is what we DO want:
  // dedupe (drops obvious duplicates), linkedErrors (walks `cause`
  // chains in our own `captureSdkException` calls), functionToString
  // (preserves stack-frame symbol names), and inboundFilters (drops
  // Sentry-internal noise). A future ticket that wants broader
  // auto-capture must wire a `beforeBreadcrumb` scrubber here at the
  // same time.
  Sentry.init({
    dsn,
    sendDefaultPii: false,
    // Build mode: "web", "tauri" (desktop) or "mobile" (iOS).
    environment: import.meta.env.MODE,
    beforeSend: scrubSentryEvent,
    defaultIntegrations: false,
    integrations: [
      Sentry.globalHandlersIntegration({ onerror: true, onunhandledrejection: true }),
      Sentry.dedupeIntegration(),
      Sentry.linkedErrorsIntegration(),
      Sentry.functionToStringIntegration(),
      Sentry.inboundFiltersIntegration(),
    ],
  });
  initialised = true;
}

/**
 * Drop high-cardinality / sensitive keys from breadcrumb data before
 * shipping. We allow only primitives (string/number/boolean/null) and
 * skip any key whose name suggests it carries secrets or message
 * payloads. This is intentionally a denylist by name — the call
 * sites pass small structured records, not arbitrary objects, so the
 * filter only needs to catch obvious misuse.
 */
// Bare `key` was previously included; dropped because no current call
// site passes a `data.key` field and it risked silently dropping a
// future descriptive identifier (e.g. an opaque cache key). The
// recovery-key path is already covered by the explicit `recoveryKey`
// match. Future call sites that introduce a sensitive `key` field
// should add a matching denylist entry alongside this comment.
const FORBIDDEN_KEY_PATTERN =
  /^(body|formattedBody|message|content|sessionJson|sessionId|recoveryKey|accessToken|token|password|secret|snapshotKey|keychainName)$/i;

function scrubBreadcrumbData(data: SdkBreadcrumbData | undefined): SdkBreadcrumbData | undefined {
  if (!data) return undefined;
  const out: SdkBreadcrumbData = {};
  for (const [key, value] of Object.entries(data)) {
    if (FORBIDDEN_KEY_PATTERN.test(key)) continue;
    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean" ||
      value === null
    ) {
      out[key] = value;
    }
  }
  return out;
}

/**
 * Coerce an unknown error into a short, stable string for breadcrumb
 * messages. We deliberately do NOT serialize non-Error throws — the
 * Rust binding can reject with structured HTTP error objects whose
 * nested `body` / `request` fields may carry tokens, message
 * payloads, or echoed homeserver request bodies. Only `Error.message`
 * and bare strings are surfaced; everything else collapses to a
 * stable sentinel so the breadcrumb still records *that* it failed
 * without leaking *what* it contained. Stack traces ride
 * `captureException`, never the breadcrumb path.
 */
function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  return "<non-error>";
}

/**
 * Record a breadcrumb describing an SDK warn-level failure. Always
 * tagged `level: "warning"` — these are non-fatal failures the SDK
 * recovers from. The breadcrumb's `message` is the same string the
 * old `console.warn` emitted; the original error message is folded
 * into `data.error` so Sentry's UI surfaces it alongside the trail.
 */
export function recordSdkBreadcrumb(
  category: SdkBreadcrumbCategory,
  message: string,
  err: unknown,
  data?: SdkBreadcrumbData,
): void {
  if (!isActive()) return;
  const scrubbed = scrubBreadcrumbData(data) ?? {};
  Sentry.addBreadcrumb({
    category,
    level: "warning",
    message,
    data: { ...scrubbed, error: errorMessage(err) },
  });
}

export function recordBootSummary(summary: ReadonlyArray<{ phase: string; ms: number }>): void {
  if (!isActive()) return;
  const flat: SdkBreadcrumbData = {};
  for (const { phase, ms } of summary) {
    if (Number.isInteger(ms)) flat[phase] = ms;
  }
  const scrubbed = scrubBreadcrumbData(flat);
  if (!scrubbed || Object.keys(scrubbed).length === 0) return;
  Sentry.addBreadcrumb({
    category: "chat.boot",
    level: "info",
    message: "chat boot",
    data: scrubbed,
  });
}

/**
 * Coerce an unknown throw into an `Error` instance suitable for
 * `Sentry.captureException`. Sentry's default serialiser will walk
 * arbitrary captured objects field-by-field and attach the result to
 * the event; that's the path through which structured Matrix HTTP
 * rejections (`{ status, body: { token }, request: { body } }`) would
 * otherwise leak. We collapse non-`Error` throws to a tagged
 * placeholder Error whose `message` is the same `<non-error>`
 * sentinel the breadcrumb path uses — Sentry sees a regular Error,
 * the original payload never reaches the wire.
 */
function toCapturedError(err: unknown): Error {
  if (err instanceof Error) return err;
  // String throws are non-`Error` throws too — they can carry token /
  // body payloads exactly like a structured rejection (the Rust
  // binding's `unsupported(method)` path throws `new Error(string)`,
  // but a `throw "..."` from anywhere upstream would arrive here as
  // a bare string). Collapse to the same placeholder so the only
  // value reaching Sentry's serialiser is a tagged sentinel.
  const placeholder = new Error("<non-error>");
  placeholder.name = "NonErrorThrow";
  return placeholder;
}

/**
 * Capture a high-signal SDK exception. Adds the failure phase as a
 * tag so the Sentry issues UI groups by phase instead of by stack
 * frame (which can be unstable across SDK upgrades). The original
 * breadcrumb trail attached to the current scope ships with the
 * event automatically. Non-`Error` throws are coerced via
 * `toCapturedError` so structured payloads never reach Sentry's
 * default serialiser.
 */
export function captureSdkException(phase: SdkExceptionPhase, err: unknown): void {
  if (!isActive()) return;
  Sentry.withScope((scope) => {
    scope.setTag("matrix.phase", phase);
    Sentry.captureException(toCapturedError(err));
  });
}

/**
 * Test hook: flip the active flag without driving a real `Sentry.init`.
 * Lets unit tests verify the call-site mapping (which category /
 * phase fires for each instrumented warn) without instantiating
 * the Sentry transport.
 */
export function __setTelemetryActiveForTests(active: boolean): void {
  initialised = active;
}
